import { useState } from "react";
import EnhancedLatexRenderer from "./EnhancedLatexRenderer";
import { TableRenderer } from "./MessageWithTableRenderer";

// Matches both single citation markers ([1]) and comma-joined ones ([1, 12]) that
// the FAQ agent's synthesis prompt sometimes emits despite being asked for one per marker.
const CITATION_REGEX = /\[(\d+(?:\s*,\s*\d+)*)\]/g;
const INLINE_REGEX = /\[(\d+(?:\s*,\s*\d+)*)\]|\*\*([^*]+)\*\*/g;

function extractCitedIndexes(text) {
    const indexes = new Set();
    for (const match of text.matchAll(CITATION_REGEX)) {
        for (const n of match[1].split(",")) indexes.add(Number(n.trim()));
    }
    return indexes;
}

// One line of answer text: bold segments render as strong, citation markers become
// small badges linking to the matching web source (or a dim [n] if unmatched).
function InlineText({ text, webSources }) {
    if (!text) return null;
    const parts = [];
    let lastIndex = 0;
    let match;
    INLINE_REGEX.lastIndex = 0;

    while ((match = INLINE_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(
                <EnhancedLatexRenderer
                    key={parts.length}
                    text={text.slice(lastIndex, match.index)}
                />,
            );
        }

        if (match[1] !== undefined) {
            match[1].split(",").forEach((raw) => {
                const n = raw.trim();
                const source = webSources?.find((s) => s.index === Number(n));
                parts.push(
                    source ? (
                        <a
                            key={parts.length}
                            href={source.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center align-super text-[0.65em] font-semibold text-orange-700 bg-orange-100 hover:bg-orange-200 rounded px-1 mx-0.5 no-underline"
                        >
                            {n}
                        </a>
                    ) : (
                        <span
                            key={parts.length}
                            className="text-gray-400 text-[0.65em] align-super mx-0.5"
                        >
                            [{n}]
                        </span>
                    ),
                );
            });
        } else if (match[2] !== undefined) {
            parts.push(
                <strong key={parts.length} className="font-semibold text-orange-600">
                    <EnhancedLatexRenderer text={match[2]} />
                </strong>,
            );
        }

        lastIndex = INLINE_REGEX.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(
            <EnhancedLatexRenderer key={parts.length} text={text.slice(lastIndex)} />,
        );
    }

    return <>{parts}</>;
}

// Paragraphs + "* "/"- " bullet lists + GFM pipe tables, each line run through InlineText.
const HEADING_REGEX = /^\s*(#{1,6})\s+(.*)$/;

function isTableRow(line) {
    return line.includes("|");
}

// GFM separator row: cells of only "-"/":" (e.g. "---" or ":---:"), pipe-delimited.
function isTableSeparatorRow(line) {
    const trimmed = line.trim();
    if (!trimmed.includes("-")) return false;
    const cells = trimmed
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map((c) => c.trim());
    return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

const HEADING_CLASS = {
    1: "text-base font-bold mt-2 mb-1",
    2: "text-[15px] font-bold mt-2 mb-1",
    3: "text-sm font-bold mt-1.5 mb-1",
    4: "text-sm font-semibold mt-1.5 mb-1",
    5: "text-xs font-semibold mt-1 mb-0.5",
    6: "text-xs font-semibold text-gray-600 mt-1 mb-0.5",
};

function AnswerBody({ text, webSources }) {
    const lines = text.split("\n");
    const elements = [];
    let currentList = [];

    const flushList = () => {
        if (currentList.length === 0) return;
        elements.push(
            <ul
                key={`list-${elements.length}`}
                className="list-disc list-inside my-1.5 space-y-1"
            >
                {currentList.map((item, idx) => (
                    <li key={idx} className="ml-4">
                        <InlineText text={item} webSources={webSources} />
                    </li>
                ))}
            </ul>,
        );
        currentList = [];
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // GFM table: a "|"-row immediately followed by a "---"-separator row.
        if (
            isTableRow(line) &&
            lines[i + 1] !== undefined &&
            isTableSeparatorRow(lines[i + 1])
        ) {
            flushList();
            const tableLines = [line, lines[i + 1]];
            let j = i + 2;
            while (j < lines.length && lines[j].trim() && isTableRow(lines[j])) {
                tableLines.push(lines[j]);
                j++;
            }
            elements.push(
                <TableRenderer
                    key={`table-${elements.length}`}
                    tableContent={tableLines.join("\n")}
                />,
            );
            i = j - 1;
            continue;
        }

        const headingMatch = line.match(HEADING_REGEX);
        if (headingMatch) {
            flushList();
            const level = headingMatch[1].length;
            elements.push(
                <div
                    key={`heading-${elements.length}`}
                    className={HEADING_CLASS[level]}
                >
                    <InlineText
                        text={headingMatch[2].trim()}
                        webSources={webSources}
                    />
                </div>,
            );
            continue;
        }
        if (/^\s*[-*]\s+/.test(line)) {
            currentList.push(line.replace(/^\s*[-*]\s+/, "").trim());
            continue;
        }
        flushList();
        if (line.trim()) {
            elements.push(
                <div key={`text-${elements.length}`} className="mb-1">
                    <InlineText text={line} webSources={webSources} />
                </div>,
            );
        } else if (elements.length > 0) {
            elements.push(
                <div key={`space-${elements.length}`} className="h-2" />,
            );
        }
    }
    flushList();

    return <div>{elements}</div>;
}

const SOURCE_BADGE = {
    db: { label: "FAQ database", className: "text-emerald-700 bg-emerald-100" },
    website_intro: {
        label: "Company website",
        className: "text-sky-700 bg-sky-100",
    },
    web_search: { label: "Web search", className: "text-amber-700 bg-amber-100" },
};

function SourceBadge({ source }) {
    const cfg = SOURCE_BADGE[source];
    if (!cfg) return null;
    return (
        <span
            className={`inline-flex items-center gap-1 w-fit text-[11px] font-medium px-2 py-0.5 rounded-full ${cfg.className}`}
        >
            {cfg.label}
        </span>
    );
}

function MatchedFaqsList({ matchedFaqs }) {
    if (!matchedFaqs || matchedFaqs.length === 0) return null;
    return (
        <ul className="text-[11px] text-gray-500 space-y-0.5">
            {matchedFaqs.map((faq) => (
                <li key={faq.questionId}>
                    <span className="text-gray-400">[{faq.displayId}]</span>{" "}
                    {faq.sourceUrl ? (
                        <a
                            href={faq.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-orange-600 hover:underline"
                        >
                            {faq.question}
                        </a>
                    ) : (
                        <span>{faq.question}</span>
                    )}
                </li>
            ))}
        </ul>
    );
}

function SourceCard({ source }) {
    return (
        <a
            href={source.url}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col shrink-0 w-36 rounded-lg border border-gray-200 bg-white overflow-hidden hover:shadow-sm transition-shadow"
        >
            <div className="p-2 flex flex-col gap-0.5">
                <span className="text-[10px] text-gray-400">
                    [{source.index}] {source.domain}
                </span>
                <span className="text-xs font-medium text-gray-800 line-clamp-2">
                    {source.title || source.domain}
                </span>
            </div>
        </a>
    );
}

function WebSourcesRow({ text, webSources }) {
    const [showOthers, setShowOthers] = useState(false);
    const citedIndexes = extractCitedIndexes(text);
    const cited = webSources.filter((s) => citedIndexes.has(s.index));
    const others = webSources.filter((s) => !citedIndexes.has(s.index));

    if (cited.length === 0 && others.length === 0) return null;

    return (
        <div className="flex flex-col gap-1.5">
            {cited.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                    {cited.map((s) => (
                        <SourceCard key={s.index} source={s} />
                    ))}
                </div>
            )}
            {others.length > 0 && (
                <div>
                    <button
                        onClick={() => setShowOthers((v) => !v)}
                        className="text-[11px] text-gray-400 hover:text-gray-600 underline-offset-2 hover:underline"
                    >
                        {showOthers
                            ? "Hide"
                            : `Show ${others.length} more searched source${others.length > 1 ? "s" : ""}`}
                    </button>
                    {showOthers && (
                        <div className="flex gap-1.5 overflow-x-auto pb-1 mt-1.5">
                            {others.map((s) => (
                                <SourceCard key={s.index} source={s} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Animated-dots stage placeholder shown while the agent hasn't streamed any real
// answer text yet — mirrors MessagesArea's existing "Thinking..." dots treatment.
export function FaqStageIndicator({ label }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-sm text-gray-600">{label}</span>
            <div className="flex space-x-1">
                {[0, 0.2, 0.4].map((delay, i) => (
                    <div
                        key={i}
                        className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"
                        style={{ animationDelay: `${delay}s` }}
                    />
                ))}
            </div>
        </div>
    );
}

export function FaqAnswerRenderer({ text, source, webSources, matchedFaqs }) {
    // A grounded-search source badge/card row is only accurate when there's
    // at least one source to back it — the model sometimes answers from its
    // own knowledge with source: "web_search" but zero webSources, in which
    // case no search actually ran and there's nothing to show provenance for.
    const hasWebSources =
        (source === "web_search" || source === "website_intro") &&
        (webSources?.length ?? 0) > 0;
    const showSourceBadge = source === "db" || hasWebSources;

    return (
        <div className="flex flex-col">
            <AnswerBody text={text} webSources={webSources} />
            {showSourceBadge && (
                <div className="mt-1.5 flex flex-col gap-1.5">
                    <SourceBadge source={source} />
                    {source === "db" && (
                        <MatchedFaqsList matchedFaqs={matchedFaqs} />
                    )}
                    {hasWebSources && (
                        <WebSourcesRow text={text} webSources={webSources} />
                    )}
                </div>
            )}
        </div>
    );
}
